import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { traAdmin, type TraWaitlistEntry, type TraCertificationEdition } from '@/lib/api/traAdminClient';
import { TraWaitlistStatusBadge } from '@/components/tra/TraWaitlistStatusBadge';
import Alert from '../ui/alert/Alert';
import ModuleLayout, { ModuleToolbar } from '../admin/ModuleLayout';import { RowActionsMenu, type RowAction } from '../admin/RowActionsMenu';
import { ConfirmDialog } from '../admin/ConfirmDialog';
import { StyledSelect } from '../admin/FormComponents';
import DataTable, { type DataTableColumn } from '../ui/table/DataTable';
import { useServerTable } from '../../hooks/useServerTable';
import { applyOptimistic, rollbackOptimistic } from '../../lib/optimisticMutation';
import { toast, toastError } from '../../lib/toast';
import { traNavTabs } from './traNavTabs';
import { EyeIcon, CloseLineIcon } from '../../icons';

interface WaitlistTablePage {
  items: TraWaitlistEntry[];
  total: number;
  page: number;
  limit: number;
}

// Reasignación inline (tra-waitlist-edition-reassignment) — una mutación por
// fila, optimista contra la misma queryKey paginada que usa useServerTable
// (ver su comentario "para optimistic updates via applyOptimistic/rollbackOptimistic").
function EditionReassignCell({
  entry,
  editions,
  queryKey,
}: {
  entry: TraWaitlistEntry;
  editions: TraCertificationEdition[];
  queryKey: QueryKey;
}) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (editionId: string | null) => traAdmin.reassignWaitlistEdition(entry.id, editionId),
    onMutate: async (editionId) => {
      const previous = await applyOptimistic<WaitlistTablePage>(qc, queryKey, (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((row) => (row.id === entry.id ? { ...row, editionId, status: 'pending' } : row)),
            }
          : prev,
      );
      return { previous };
    },
    onError: (_err, _editionId, context) => {
      rollbackOptimistic(qc, queryKey, context?.previous);
      toastError('No se pudo reasignar la edición. Intenta de nuevo.');
    },
    onSuccess: (updated) => {
      if (updated.revokedPreviousEnrollment) {
        toast.info('Se revocó su acceso al semillero de la cohorte anterior.');
      }
    },
  });

  return (
    <StyledSelect
      value={entry.editionId ?? ''}
      onChange={(e) => mutation.mutate(e.target.value || null)}
      disabled={mutation.isPending}
    >
      <option value="">Ninguna</option>
      {editions.map((ed) => (
        <option key={ed.id} value={ed.id}>{ed.name}</option>
      ))}
    </StyledSelect>
  );
}

const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'Todos los estados', value: '' },
  { label: 'Pendiente', value: 'pending' },
  { label: 'Seleccionado', value: 'selected' },
  { label: 'En espera', value: 'waitlisted' },
  { label: 'Rechazado', value: 'rejected' },
];

interface WaitlistTableProps {
  /** Cuando se da, fija el filtro de edición y oculta su selector (uso embebido en EditionDetailPage). */
  editionId?: string;
}

// Extraído de WaitlistListPage (tra-edition-tabbed-detail) para poder embeberse
// en la tab Participantes de una edición con editionId fijo, sin duplicar la
// lógica de tabla/columnas/toolbar.
export function WaitlistTable({ editionId: fixedEditionId }: WaitlistTableProps) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [editionFilter, setEditionFilter] = useState('');
  const [editions, setEditions] = useState<TraCertificationEdition[]>([]);  const [removeCandidate, setRemoveCandidate] = useState<TraWaitlistEntry | null>(null);
  const qc = useQueryClient();

  function goToAddEntry() {
    navigate(fixedEditionId ? `/tra/waitlist/new?editionId=${fixedEditionId}` : '/tra/waitlist/new');
  }

  const editionId = fixedEditionId ?? editionFilter;

  const {
    rows: items,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    reload,
    queryKey,
    payload,
    error,
  } = useServerTable<TraWaitlistEntry>({
    endpoint: '/tra/certification-waitlist',
    defaultPageSize: 20,
    defaultSort: { column: 'createdAt', dir: 'desc' },
    params: { status, search, city, editionId: editionId || undefined },
    extractRows: (data) => data.items ?? [],
    extractTotal: (data) => data.total ?? 0,
  });

  useEffect(() => {
    if (fixedEditionId) return;
    traAdmin
      .listEditions({ limit: 50, page: 1 })
      .then((data) => setEditions(data.items ?? []))
      .catch(() => setEditions([]));
  }, [fixedEditionId]);

  // "Retirar de la edición" en el menú de acciones — solo dentro de la tab
  // Participantes de una edición (fixedEditionId), donde ya no hay un
  // selector visible para hacerlo (la columna "Edición" está oculta ahí).
  // Confirmación vía ConfirmDialog: es de un solo clic en un menú, a
  // diferencia del <select> inline de EditionReassignCell, que es una
  // elección deliberada de valor.
  const removeMutation = useMutation({
    mutationFn: (entry: TraWaitlistEntry) => traAdmin.reassignWaitlistEdition(entry.id, null),
    onMutate: async (entry) => {
      const previous = await applyOptimistic<WaitlistTablePage>(qc, queryKey, (prev) =>
        prev
          ? { ...prev, items: prev.items.map((row) => (row.id === entry.id ? { ...row, editionId: null, status: 'pending' } : row)) }
          : prev,
      );
      return { previous };
    },
    onError: (_err, _entry, context) => {
      rollbackOptimistic(qc, queryKey, context?.previous);
      toastError('No se pudo retirar de la edición. Intenta de nuevo.');
    },
    onSuccess: (updated) => {
      if (updated.revokedPreviousEnrollment) {
        toast.info('Se revocó su acceso al semillero de esta cohorte.');
      }
      setRemoveCandidate(null);
    },
  });

  // Totales globales por estado (no dependen de la página, la búsqueda ni los filtros); los entrega el servidor.
  const summary = (payload as { summary?: { total: number; pending: number; selected: number; waitlisted: number } } | undefined)?.summary;
  const pendingCount = summary?.pending ?? 0;

  const columns: DataTableColumn<TraWaitlistEntry>[] = [
    {
      key: 'fullName',
      label: 'Nombre',
      sortable: true,
      render: (entry) => (
        <>
          <p className="font-medium text-gray-900 dark:text-white">{entry.fullName}</p>
          <p className="text-xs text-gray-500">{entry.email}</p>
        </>
      ),
    },
    {
      key: 'city',
      label: 'Ciudad',
      sortable: true,
      render: (entry) => <span className="text-gray-600 dark:text-gray-400">{entry.city}</span>,
    },
    {
      key: 'educationalInstitution',
      label: 'Institución',
      render: (entry) => (
        <span className="text-gray-600 dark:text-gray-400">{entry.educationalInstitution}</span>
      ),
    },
    {
      key: 'cedula',
      label: 'Cédula',
      render: (entry) => <span className="text-gray-600 dark:text-gray-400">{entry.cedula}</span>,
    },
    {
      key: 'status',
      label: 'Estado',
      sortable: true,
      render: (entry) => <TraWaitlistStatusBadge status={entry.status} />,
    },
    ...(fixedEditionId
      ? []
      : [
          {
            key: 'edition',
            label: 'Edición',
            render: (entry: TraWaitlistEntry) => (
              <EditionReassignCell entry={entry} editions={editions} queryKey={queryKey} />
            ),
          } satisfies DataTableColumn<TraWaitlistEntry>,
        ]),
    {
      key: 'createdAt',
      label: 'Registro',
      sortable: true,
      render: (entry) => (
        <span className="text-xs text-gray-500">
          {new Date(entry.createdAt).toLocaleDateString('es-CO')}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Acción',
      align: 'right',
      render: (entry) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              { label: 'Ver solicitud', icon: <EyeIcon />, to: `/tra/waitlist/${entry.id}` },
              ...(fixedEditionId
                ? ([
                    {
                      label: 'Retirar de la edición',
                      icon: <CloseLineIcon />,
                      variant: 'danger',
                      requires: 'tripoli:waitlist:edit',
                      onClick: () => setRemoveCandidate(entry),
                    },
                  ] satisfies RowAction[])
                : []),
            ]}
          />
        </div>
      ),
    },
  ];

  const table = (
    <>
      {error ? (
        <div className="mb-4">
          <Alert variant="error" title="No se pudo cargar la lista de espera" message={error} />
        </div>
      ) : null}
      <DataTable
        columns={columns}
        rows={items}
        rowKey={(entry) => entry.id}
        loading={loading}
        emptyMessage="No se encontraron solicitudes."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={total}
        onPageChange={setPage}
      />      <ConfirmDialog
        isOpen={removeCandidate !== null}
        title={`¿Retirar a "${removeCandidate?.fullName ?? ''}" de esta edición?`}
        message="Vuelve a la lista de espera general en estado Pendiente y se revoca su acceso al semillero de esta cohorte, si lo tenía."
        confirmLabel="Retirar"
        confirming={removeMutation.isPending}
        onConfirm={() => removeCandidate && removeMutation.mutate(removeCandidate)}
        onCancel={() => setRemoveCandidate(null)}
      />
    </>
  );

  // Embebida en una tab de edición: sin ModuleLayout propio (el shell de la
  // edición ya provee breadcrumbs/tabs/metrics), solo el toolbar de filtros y la tabla.
  if (fixedEditionId) {
    return (
      <div className="space-y-4">
        <ModuleToolbar
          toolbar={{
            search: {
              value: search,
              onChange: setSearch,
              placeholder: 'Buscar por nombre, correo o cédula...',
            },
            filters: [
              {
                type: 'select',
                key: 'status',
                label: 'Estado',
                value: status,
                onChange: setStatus,
                options: STATUS_OPTIONS,
              },
              {
                type: 'text',
                key: 'city',
                label: 'Ciudad',
                value: city,
                onChange: setCity,
                placeholder: 'Filtrar por ciudad...',
              },
            ],
            onCreate: goToAddEntry,
            createLabel: '+ Agregar interesado',
          }}
        />
        {table}
      </div>
    );
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, 'TRA', 'Lista de espera']}
      title="Lista de espera"
      subtitle="Solicitudes de certificación L1 pendientes de revisión"
      tabs={traNavTabs('waitlist')}
      metrics={[
        { label: 'Solicitudes', value: summary ? String(summary.total) : '—' },
        {
          label: 'Pendientes',
          value: summary ? String(pendingCount) : '—',
          tone: pendingCount > 0 ? 'warning' : 'good',
          hint: 'Esperando decisión: seleccionar o rechazar',
        },
        { label: 'Seleccionadas', value: summary ? String(summary.selected) : '—', tone: 'good' },
        { label: 'En lista de espera', value: summary ? String(summary.waitlisted) : '—' },
      ]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: 'Buscar por nombre, correo o cédula...',
        },
        filters: [
          {
            type: 'select',
            key: 'status',
            label: 'Estado',
            value: status,
            onChange: setStatus,
            options: STATUS_OPTIONS,
          },
          {
            type: 'select',
            key: 'edition',
            label: 'Edición',
            value: editionFilter,
            onChange: setEditionFilter,
            options: [
              { label: 'Todas las ediciones', value: '' },
              ...editions.map((e) => ({ label: e.name, value: e.id })),
            ],
          },
          {
            type: 'text',
            key: 'city',
            label: 'Ciudad',
            value: city,
            onChange: setCity,
            placeholder: 'Filtrar por ciudad...',
          },
        ],
        onRefresh: reload,
        refreshing: loading,        onCreate: goToAddEntry,
        createLabel: '+ Agregar interesado',
      }}
    >
      {table}
    </ModuleLayout>
  );
}
